import { columnOptions } from '../pages/typings/options.typings'

export const defaultOptionChainColumns = [
    'bid',
    'ask',
    'ltp',
    'ltpChange',
    'openInterest',
    'oiChange|outlook',
    'volume',
    'impliedVolatility',
    'delta',
    'gamma',
    'vega',
    'theta',
    'rho',
]

export const defaultExtendedOptionChainColumns = ['ltp', 'ltpChange', 'impliedVolatility', 'delta', 'openInterest']

export const expandColumnOptionValues = (): string[] => {
    const result: string[] = []
    columnOptions.forEach((c) => {
        if (c.value === 'bidAsk') {
            result.push('bid', 'ask')
        } else {
            result.push(c.value)
        }
    })
    return result
}

export const getAllOptionChainColumnIds = (type: 'extended' | 'normal'): string[] =>
    type === 'extended' ? [...defaultExtendedOptionChainColumns] : expandColumnOptionValues()

export const getCanonicalOptionChainColumnOrder = (type: 'extended' | 'normal'): string[] =>
    type === 'extended' ? defaultExtendedOptionChainColumns : defaultOptionChainColumns

export const insertColumnPreservingOrder = (
    columns: string[],
    column: string,
    canonicalOrder: string[]
): string[] => {
    if (columns.includes(column)) return columns
    const targetIndex = canonicalOrder.indexOf(column)
    if (targetIndex === -1) return [...columns, column]
    let insertAt = 0
    for (let i = 0; i < columns.length; i++) {
        const colIndex = canonicalOrder.indexOf(columns[i])
        if (colIndex !== -1 && colIndex < targetIndex) {
            insertAt = i + 1
        }
    }
    return [...columns.slice(0, insertAt), column, ...columns.slice(insertAt)]
}

export const mergeIntoColumnOrder = (
    order: string[],
    allColumns: string[],
    canonicalOrder: string[]
): string[] => {
    const missing = allColumns.filter((col) => !order.includes(col))
    if (missing.length === 0) return order
    let result = order
    const orderSet = new Set(order)
    missing.forEach((col) => {
        result = insertColumnPreservingOrder(result, col, canonicalOrder)
        orderSet.add(col)
    })
    return result
}

export const getVisibleOptionChainColumns = (order: string[], hidden: string[]): string[] => {
    if (hidden.length === 0) return order
    const hiddenSet = new Set(hidden)
    return order.filter((c) => !hiddenSet.has(c))
}

export const columnsToHide = (columnKey: string): string[] =>
    columnKey === 'bid' || columnKey === 'bidAsk' ? ['bid', 'ask'] : [columnKey]

const MERGED_OI_OUTLOOK_COL = 'oiChange|outlook'

/** Merge separate OI change / outlook columns into the combined column. */
export const normalizeOptionChainColumnOrder = (order: string[]): string[] => {
    const result: string[] = []
    let mergedAdded = false
    order.forEach((col) => {
        if (col === 'oiChange' || col === 'outlook' || col === MERGED_OI_OUTLOOK_COL) {
            if (!mergedAdded) {
                result.push(MERGED_OI_OUTLOOK_COL)
                mergedAdded = true
            }
            return
        }
        if (!result.includes(col)) result.push(col)
    })
    return result
}

export const normalizeOptionChainHiddenColumns = (hidden: string[]): string[] => {
    const hidesOi = hidden.includes('oiChange')
    const hidesOutlook = hidden.includes('outlook')
    const hidesMerged = hidden.includes(MERGED_OI_OUTLOOK_COL)
    if (!hidesOi && !hidesOutlook && !hidesMerged) return hidden
    const next = hidden.filter((c) => c !== 'oiChange' && c !== 'outlook' && c !== MERGED_OI_OUTLOOK_COL)
    if (hidesOi || hidesOutlook || hidesMerged) {
        next.push(MERGED_OI_OUTLOOK_COL)
    }
    return next
}
